import mapImage from 'assets/img/agent-landing/zip-code/map.png';
import { container } from 'assets/jss/material-dashboard-pro-react';
import {
  Field, FieldProps, Form, Formik,
} from 'formik';
import React from 'react';
import { toast } from 'sonner';
import { IAgentLandingZipCodeData } from 'types/agent-landing-page';
import * as Yup from 'yup';

import { makeStyles } from '@material-ui/core/styles';

import Button from 'creative-components/CustomButtons/Button';
import CustomInput from 'creative-components/CustomInput/CustomInput';
import GridContainer from 'creative-components/Grid/GridContainer';
import GridItem from 'creative-components/Grid/GridItem';

import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import { trackCustomEvent } from 'components/MetaPixelWrapper';

import { getAgentLandingZipCodeData } from 'utils/api';

const useStyles = makeStyles((theme) => ({
  container: {
    ...container,
    position: 'relative',
  },
  card: {
    position: 'relative',
    padding: '70px 80px !important',
    background: `linear-gradient(rgba(255,255,255,0.9), rgba(255,255,255,0.9)), url(${mapImage})`,
    filter: 'drop-shadow(0px 4px 24px rgba(0, 0, 0, 0.25))',
    borderRadius: '8px',

    [theme.breakpoints.down('sm')]: {
      padding: '35px 40px !important',
    },
  },
  centered: {
    '& > div': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
    },
  },
  title: {
    fontFamily: 'Vidaloka',
    fontSize: '42px',
    lineHeight: '50px',
    color: theme.palette.primary.main,
    marginBottom: '20px',

    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
  subtitle: {
    fontSize: '18px',
    fontWeight: 500,
    color: theme.palette.black.main,
    marginBottom: '30px',

    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
  loadingMessage: {
    fontWeight: 500,
    fontSize: '24px',
    lineHeight: '32px',
    color: theme.palette.darkBlueGray.main,
    marginBottom: '20px',
  },
  input: {
    width: '100%',
    maxWidth: 350,
    margin: 'auto',
  },
  buttonContainer: {
    textAlign: 'center',

    '& > button': {
      margin: '32px 0 0',

      [theme.breakpoints.up('lg')]: {
        minWidth: '400px',
      },
    },
  },
}));

interface FormValues {
  zipCode: string;
  recaptchaValue: string;
}

type Props = {
  agentLandingPageToken: string;
  setZipCodeData: React.Dispatch<React.SetStateAction<IAgentLandingZipCodeData | undefined>>;
};

const DigitalAdZipCodeForm = ({ agentLandingPageToken, setZipCodeData }: Props) => {
  const classes = useStyles();
  return (
    <GridContainer className={classes.container}>
      <GridItem xs={12} className={classes.card}>
        <Formik
          validateOnChange={false}
          validateOnBlur={false}
          initialValues={{
            recaptchaValue: '',
            zipCode: '',
          } as FormValues}
          onSubmit={async ({
            zipCode, recaptchaValue,
          }) => {
            try {
              // Fire off a custom event for the meta pixel
              trackCustomEvent('ZipCodeFormSubmitted');

              setZipCodeData(await getAgentLandingZipCodeData(agentLandingPageToken, zipCode, recaptchaValue));
            } catch (error: any) {
              if (error.response && error.response.data.error) {
                toast.error(error.response.data.error);
              } else {
                toast.error('Something went wrong, please try again.');
              }

              console.error('error', error);
            }
          }}
          validationSchema={Yup.object().shape({
            zipCode: Yup.string() // Strip non-digits, then require exactly 5 digits
              .transform((val) => (val ? val.replace(/\D/g, '') : val))
              .matches(/^\d{5}$/, 'Zip code must be exactly 5 digits')
              .required('Zip code is required'),
            recaptchaValue: Yup.string(), // .required(),
          })}
        >
          {(props) => {
            const { handleSubmit, isSubmitting, setFieldValue } = props;

            return (
              <Form>
                {!isSubmitting ? (
                  <GridContainer className={classes.centered}>
                    <GridItem xs={12}>
                      <h3 className={classes.title}>Get Your Real-Time Market Profile</h3>
                      {/* <h3 className={classes.subtitle}>Find out how your local real estate market is performing</h3> */}
                    </GridItem>

                    <GridItem xs={12} className={classes.input}>
                      <Field name="zipCode">
                        {({ field, meta }: FieldProps) => (
                          <CustomInput
                            labelBold
                            labelRequired
                            inputProps={{
                              ...field,
                              type: 'text', // keep as text, control allowed chars manually
                              inputMode: 'numeric', // mobile keypad
                              pattern: '[0-9]*', // hint for browsers
                              placeholder: 'Enter Zip Code',
                              autoComplete: 'postal-code',
                              maxLength: 5,
                              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                // Allow only digits and limit to 5
                                const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                                setFieldValue(field.name, value);
                              },
                            }}
                            error={!!meta.touched && meta.error !== undefined}
                            helperText={meta.touched && meta.error ? meta.error : undefined}
                          />
                        )}
                      </Field>
                    </GridItem>

                    {/* <GridItem xs={12}>
                      <Field name="recaptchaValue">
                        {({ field, meta }: FieldProps) => (
                          <ReCAPTCHAInput
                            fieldName={field.name}
                          />
                        )}
                      </Field>
                    </GridItem> */}

                    <GridItem xs={12} className={classes.buttonContainer}>
                      <Button
                        color="dark"
                        round
                        size="large"
                        disabled={isSubmitting}
                        onClick={() => handleSubmit()}
                      >
                        See My Market Data
                      </Button>
                    </GridItem>
                  </GridContainer>
                ) : (
                  <div style={{ textAlign: 'center', margin: '40px auto' }}>
                    <div className={classes.loadingMessage}>Hang tight! We're gathering market data for your zip code...</div>
                    <LoadingIndicator />
                  </div>
                )}
              </Form>
            );
          }}
        </Formik>
      </GridItem>
    </GridContainer>
  );
};

export default DigitalAdZipCodeForm;
